/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import config from "../config/featuresSection.config.json";
import { VApplication } from "../../../application";
import { BreadcrumbService } from "../../breadcrumbService";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_POLICY_HOLDER_DETAIL } from "../graphql";
import { toHeaderCase, toLowerCase, toSentenceCase } from "js-convert-case";
import { useUserId } from "../provider";

import { AppNavigatorService } from "../../appNavigatorService";

const getWhoamiSrv = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.AppNavigatorService") as AppNavigatorService;
};

export interface IFeatureSectionMethods extends IPluginMethodMap {
  getFeatureSectionProps(args: any): Promise<any>;
}

const lookup = require("country-code-lookup");

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export class FeatureSectionService implements IAgentPlugin {
  readonly methods: IFeatureSectionMethods;
  constructor() {
    this.methods = {
      getFeatureSectionProps: this.getProps.bind(this),
    };
  }

  async getProps(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    const { userId } = useUserId();
    try {
      if (client) {
        const res = await client.query({
          query: GET_POLICY_HOLDER_DETAIL,
          variables: {
            input: {
              vuid: userId,
            },
          },
          fetchPolicy: "network-only",
        });
        if (res?.data?.getUser) {
          let _res = res?.data?.getUser;
          return {
            title: `${toHeaderCase(_res?.firstName ?? "-")} ${toHeaderCase(
              _res?.lastName ?? "-"
            )}`,
            subtitle: _res?.occupation,
            kycSubmitted:_res?.kycSubmitted,
            profileStatus: this.getStatus(_res?.kyc),
            profileStatusDescription: this.getStatusDesc(_res?.kyc),
            heroImage:{
              image:
              _res?.image ??
              `https://ui-avatars.com/api/?name=${_res.firstName}+${_res?.lastName}&background=0D8ABC&color=fff`
            },            
            listItems: this.getListItems(_res),
            carouselItems: _res?.kycSubmitted ? this.getCarouselItems(_res?.kyc):null,
            buttons: await this.getButtons(_res),
          };
        }
      }
    } catch (err: any) {
      console.log(`Sign In user error`, err?.response?.data);
      throw Error(err?.response?.data?.error?.display_message);
    }
  }

  private getCarouselItems(items: any) {
    let _temp = [];
    _temp =
      Array.isArray(items) &&
      items?.map((ele: any) => {
        return {
          ...ele,
          type: this.getDocType(ele?.fileType),
          src: ele?.doc,
          title: ele?.name,
          poster: ele?.poster,
        };
      });
    return _temp;
  }

  private getDocType(res:any) {
    switch(res){
      case "application/pdf":
        return "pdf";
      case "image/png":
        return "img";
      case "image/jpg":
        return "img";
      case "image/jpeg":
        return "img";
      default: return null;
    }
  }

  private async getButtons(res: any) {
    let _buttons: any = [];
    let _temp1 = res.kyc?.find((i) => i?.type === "ADDRESS_PROOF");
    let whoAmI = await getWhoamiSrv()?.whoAmI();
    if (whoAmI?.whoami === "RISK_CAPITAL") {
      return [];
    } else if (_temp1?.status === "SUBMITTED") {
      _buttons.push(config?.approveButton);
    } else if (_temp1?.status === "REJECTED") {
      _buttons.push(config?.approveButton);
    }
    return _buttons;
  }

  private getStatus(items: any) {
    let tempStatus = items && items[0];
    if (tempStatus?.status) {
      return toLowerCase(tempStatus?.status ?? "");
    } else return null;
  }

  private getStatusDesc(items: any) {
    let tempStatus = items && items[0];
    if (tempStatus?.comment) {
      return toSentenceCase(tempStatus?.comment ?? "");
    } else return null;
  }

  private getListItems(res: any) {
    let _temp = [];
    _temp = config?.listItems?.map((item) => {
      if (item?.title === "Full Name") {
        return {
          ...item,
          subTitle: `${toHeaderCase(res?.firstName ?? "")} ${toHeaderCase(
            res?.lastName ?? ""
          )}`,
        };
      }
      if (item?.title === "Street Address") {
        return {
          ...item,
          subTitle: res?.address?.addressLine1
            ? `${res?.address?.addressLine1}`
            : "-",
        };
      }
      if (item?.title === "City") {
        return {
          ...item,
          subTitle: res?.address?.city ?? "-",
        };
      }
      if (item?.title === "Country") {
        return {
          ...item,
          // subTitle:
          //   res?.address?.country && res?.address?.country.length === 2
          //     ? lookup.byIso(`${res?.address?.country}`)?.country
          //     : "-",
          // flag: res?.address?.country,
          subTitle: "USA",
        flag: "us",
        };
      }
      if (item?.title === "Email") {
        return {
          ...item,
          subTitle: res?.email ?? "-",
          verified: res?.emailVerified,
        };
      }
      if (item?.title === "Telephone") {
        return {
          ...item,
          subTitle: res?.primaryContactNo ?? "-",
          verified: res?.phoneVerified,
        };
      }
      if (item?.title === "Occupation") {
        return {
          ...item,
          subTitle: res?.occupation ?? "-",
        };
      }
    });
    return _temp;
  }
}
